/**
 * "Signed in" hint for the landing site (www.weavewords.org).
 *
 * The landing is a separate static site on another origin, so it can't see the
 * app's session (the JWT lives in app.weavewords.org's localStorage). This sets
 * a cookie on the parent domain that says only "someone is signed in here" —
 * no token, no user id — so the landing can swap "Log in / Get started" for
 * "Open app" (QA report 2026-11, #1).
 *
 * Only on *.weavewords.org (a Domain cookie can't be set from localhost or a
 * preview host), and only with the "preferences" cookie consent.
 */
export const SIGNED_IN_COOKIE = 'ww_signed_in';
const PARENT_DOMAIN = 'weavewords.org';
const MAX_AGE_S = 30 * 24 * 60 * 60; // the JWT's lifetime; the cap when its exp can't be read

function onWeavewordsDomain(hostname: string): boolean {
    return hostname === PARENT_DOMAIN || hostname.endsWith(`.${PARENT_DOMAIN}`);
}

/** The JWT's `exp` in ms, or null if the token can't be read. Not verified — only used to time the hint. */
export function tokenExpiresAt(token: string | null | undefined): number | null {
    if (!token) return null;
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof json?.exp === 'number' ? json.exp * 1000 : null;
    } catch {
        return null;
    }
}

/**
 * The hint expires with the session: at the token's `exp` when known (JWTs aren't refreshed, so
 * re-setting a fresh 30 days on every app load would outlive the session by up to 30 days).
 */
export function syncSignedInHint(
    signedIn: boolean,
    allowed: boolean,
    doc: Document = document,
    expiresAtMs: number | null = null,
    now: number = Date.now(),
): void {
    const hostname = doc.location?.hostname ?? '';
    if (!onWeavewordsDomain(hostname)) return;
    const base = `${SIGNED_IN_COOKIE}=; Domain=.${PARENT_DOMAIN}; Path=/; SameSite=Lax; Secure`;
    const maxAge = expiresAtMs == null ? MAX_AGE_S : Math.min(MAX_AGE_S, Math.floor((expiresAtMs - now) / 1000));
    if (signedIn && allowed && maxAge > 0) {
        doc.cookie = base.replace(`${SIGNED_IN_COOKIE}=;`, `${SIGNED_IN_COOKIE}=1;`) + `; Max-Age=${maxAge}`;
    } else {
        doc.cookie = `${base}; Max-Age=0`;
    }
}
